import React from 'react';
import { applyStyles, isSP } from 'util/helpers';
import { css } from 'styled-components';
import CustomMedia from 'util/custom-media-query';
import Jump from 'components/common/Jump';
import i18n from 'i18n';
import Breadcrumbs from 'components/common/Breadcrumbs';
import GrayHeader from 'components/common/GrayHeader';
import Colors from 'constants/colors';
import FontStyles from 'constants/font-styles';

const specified = ({ className, location: { pathname } }) => (
  <div className={className}>
    <GrayHeader text={i18n.t('specified:grayHeader')} />
    <Breadcrumbs currentPath={pathname} />
    <Jump />
    <ul className="specified-ul">
      {i18n.t('specified:content', { returnObjects: true }).map((item, i) => (
        <li key={i}>
          <p className="title">{item.title}</p>
          {item.content && <p className="content">{item.content}</p>}
          {item.inquiriesContent && (
            <table>
              <tbody>
                <tr>
                  <td>
                    {item.inquiriesContent.url.title}
                    <i>：</i>
                  </td>
                  <td>
                    <i>：</i>
                    <a target="blank" href={item.inquiriesContent.url.content}>
                      {item.inquiriesContent.url.content}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td rowspan="3">
                    <label>{item.inquiriesContent.product.title}</label>
                    <i>：</i>
                  </td>
                  <td>
                    <i>：</i>
                    {item.inquiriesContent.product.content}
                  </td>
                </tr>
                <tr>
                  <td className="tel">
                    <i>&nbsp;</i> 
                    <span>{item.inquiriesContent.tel.title}</span>
                    {isSP() ?
                      <a target="blank" href={`tel:${i18n.t(item.inquiriesContent.tel.content)}`}>
                        {item.inquiriesContent.tel.content}
                      </a> 
                      : 
                      `${i18n.t(item.inquiriesContent.tel.content)}`
                    }
                    {item.inquiriesContent.tel.time}
                  </td>
                </tr>
                <tr>
                  <td className="mail">
                    <i>&nbsp;</i> 
                    <span>{item.inquiriesContent.mail.title}</span>
                    <a href={`mailto:${i18n.t(item.inquiriesContent.mail.content)}`}>
                      {item.inquiriesContent.mail.content}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {item.commodityContent && (
            <table>
              <tbody>
                <tr>
                  <td>
                    {item.commodityContent.gift.title}
                    <i>：</i>
                  </td>
                  <td>
                    <i>：</i>
                    {item.commodityContent.gift.content}
                  </td>
                </tr>
                <tr>
                  <td>
                    {item.commodityContent.cost.title}
                    <i>：</i>
                  </td>
                  <td>
                    <i>：</i>
                    {item.commodityContent.cost.content}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {item.paymentAdditions && (
            <React.Fragment>
              {item.paymentAdditions.map((addition, j) => (
                <ul key={`${i}${j}`} className="row">
                  <li className="col">
                    <p>{addition.a.title}</p>
                    {addition.a.tip && <p className="tip">{addition.a.tip}</p>}
                  </li>
                  <li className="col">
                    <p>{addition.b.title}</p>
                    {addition.b.tip && <p className="tip">{addition.b.tip}</p>}
                  </li>
                </ul>
              ))}
            </React.Fragment>
          )}
          {item.tip && <p className="tip">{item.tip}</p>}
        </li>
      ))}
    </ul>
  </div>
);

const commonStyle = css`
  ul.specified-ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li:last-child {
      padding-bottom: 0;
    }

    i {
      font-style: normal;
    }

    a {
      color: ${Colors.orange1};
    }

    p {
      margin: 0;
      color: ${Colors.black1};
    }

    .title {
      ${FontStyles.text5};
    }

    table tr td{
      &:nth-child(2) {
        color: ${Colors.black1};
      }

      &.tel {
        font-weight: bold;

        span {
          margin-right: 24px;
        }
      }

      &.mail {
        font-weight: bold;
        
        span {
          margin-right: 6px;
        }
      }

      a {
        text-decoration: none;
      }
    }

    ul.row {
      height: 56px;
      width: 100%;
      display: table;
      margin: 0;
      padding: 0;
      border-bottom: 1px solid ${Colors.gray3};

      li.col {
          vertical-align: middle;
        text-align: left;
        list-style: none;

        &:nth-child(1) {
          background: ${Colors.gray4};
        }
      }
    }

    ul.row:first-of-type {
      border-top: 1px solid ${Colors.gray3};
    }
  }

  .tip {
    padding-left: 1.25em;
    text-indent: -1.25em;
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  max-width: 886px;
  margin-left: auto;
  margin-right: auto;

  ${Breadcrumbs} {
    margin-top: 12px;
  }

  ul.specified-ul {
    padding: 42px 0 160px 0;

    li {
      padding-bottom: 33px;
    }

    .title {
      padding-bottom: 6px;
    }

    table tr td {
      vertical-align: top;
      
      &:nth-child(1) {
        i {
          display: none;
        }
      }

      &:nth-child(2) {
        ${FontStyles.text7};
      }

      &.tel,
      &.mail {
         i {
           width: 16px;
           display: inline-block;
         }
      }
    }

    .tip {
      ${FontStyles.text10};

      padding-top: 1px;
    }

    ul.row li.col {
      display: table-cell;
      ${FontStyles.text7};
      height: 108px;
      padding: 15px 16px;

      .tip {
        ${FontStyles.text10};
      }

      &:nth-child(1) {
        width: 50%;
      }

      &:nth-child(2) {
        ul {
          ${FontStyles.text10};
        }
      }
    }
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  margin: 0 16px;

  ${Breadcrumbs} {
    margin-top: 6px;
  }

  ul.specified-ul {
    padding: 26px 0 48px 0;

    li {
      padding-bottom: 24px;
    }

    .title {
      padding-bottom: 4px;
    }

    .tip {
      ${FontStyles.text8};

      padding-top: 9px;
    }

    table tr td {
      display: block;

      &:nth-child(2) {
        i {
          display: none;
        }
      }

      &:nth-child(2) {
        ${FontStyles.text5};
      }
    }

    ul.row {
      padding-bottom: 10px;
      border: 1px solid ${Colors.gray3};
      padding: 16px;
      margin: 3px 0 16px 0;

      li.col {
        display: block;
        ${FontStyles.text5};
        padding: 12px 8px 10px;

        .tip {
          ${FontStyles.text8};
        }

        &:nth-child(1) {
          border-top: 1px solid ${Colors.gray3};
          width: 100%;
        }
      }
    }

    ul.row:last-child {
      margin-bottom: 6px;
    }
  }
`;

export default applyStyles(specified)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/Specified/index.js